import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Search, Wrench } from 'lucide-react';
import platformMcpService from '../../services/platformMcpService';
import { describeApiError } from '../../utils/apiError';
import InfoTip from '../shared/InfoTip';
import ToolAccessBadge from '../PlatformMcp/ToolAccessBadge';
import { DELETE_TOOLS, PLATFORM_MCP_COPY, TOO_MANY_TOOLS } from '../../constants/platformMcpCopy';
import styles from './PlatformAdmin.module.scss';
import mcpStyles from './PlatformMcpPanel.module.scss';

/**
 * Switch Quadroid's tools on or off (spec §4.4). Name, instructions and link
 * come from code and are re-applied on every setup, so nothing else is
 * editable here. `onChanged` lets the status card refresh its tool count.
 */
const PlatformMcpToolsSection = ({ exists, notify, onChanged }) => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformMcpService.listTools();
      setTools(Array.isArray(res?.tools) ? res.tools : []);
    } catch (err) {
      setError(describeApiError(err, "Couldn't load the tools."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (exists) load();
    else setLoading(false);
  }, [exists, load]);

  const enabledCount = tools.filter((t) => t.enabled).length;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tools;
    return tools.filter((t) =>
      [t.title, t.name, t.description].some((v) => (v || '').toLowerCase().includes(q))
    );
  }, [tools, query]);

  const toggle = async (tool, enabled) => {
    setPending((p) => ({ ...p, [tool.id]: true }));
    setTools((list) => list.map((t) => (t.id === tool.id ? { ...t, enabled } : t)));
    try {
      const updated = await platformMcpService.setToolEnabled(tool.id, enabled);
      if (updated?.id) setTools((list) => list.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
      onChanged?.();
    } catch (err) {
      setTools((list) => list.map((t) => (t.id === tool.id ? { ...t, enabled: tool.enabled } : t)));
      notify(describeApiError(err, `Couldn't change "${tool.title || tool.name}".`), 'error');
    } finally {
      setPending((p) => {
        const next = { ...p };
        delete next[tool.id];
        return next;
      });
    }
  };

  if (!exists) {
    return (
      <Box className={styles.emptyState}>
        <Wrench className={styles.emptyIcon} size={44} />
        <Typography variant="body2">Set up the Quadroid server first to manage its tools.</Typography>
      </Box>
    );
  }

  if (loading && tools.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={mcpStyles.section}>
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {enabledCount > TOO_MANY_TOOLS && <Alert severity="warning">{PLATFORM_MCP_COPY.tooManyTools}</Alert>}

      <Box className={styles.toolbar}>
        <TextField
          size="small"
          placeholder="Search tools"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" className={styles.muted}>
          {enabledCount} of {tools.length} tools on
        </Typography>
      </Box>

      <Box className={styles.tableCard}>
        {visible.map((t) => (
          <Box key={t.id} className={mcpStyles.toolRow}>
            <Switch
              checked={!!t.enabled}
              disabled={!!pending[t.id]}
              onChange={(e) => toggle(t, e.target.checked)}
              inputProps={{ 'aria-label': `Turn ${t.title || t.name} ${t.enabled ? 'off' : 'on'}` }}
            />
            <Box className={mcpStyles.toolText}>
              <Typography className={mcpStyles.toolTitle}>
                {t.title || t.name}
                {DELETE_TOOLS.includes(t.name) && <InfoTip title={PLATFORM_MCP_COPY.deleteToolsTip} size={14} />}
              </Typography>
              <span className={styles.mono}>{t.name}</span>
              {t.description && <span className={styles.muted}>{t.description}</span>}
            </Box>
            <ToolAccessBadge readOnly={!!t.read_only} />
          </Box>
        ))}
        {visible.length === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" className={styles.muted}>
              {tools.length ? `No tools match “${query}”.` : 'No tools yet. Click Update on the Server tab.'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PlatformMcpToolsSection;
